// User roles
export enum UserRole {
  SuperAdmin = 'SuperAdmin',
  Admin = 'Admin',
  Staff = 'Staff'
}

// User status
export enum UserStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Suspended = 'Suspended'
}

// Order status
export enum OrderStatus {
  Pending = 'Pending',
  Confirmed = 'Confirmed',
  Preparing = 'Preparing',
  Ready = 'Ready',
  Served = 'Served',
  Completed = 'Completed',
  Cancelled = 'Cancelled'
}

// Payment method
export enum PaymentMethod {
  Cash = 'Cash',
  Card = 'Card',
  UPI = 'UPI',
  Wallet = 'Wallet'
}

// Table shape
export enum TableShape {
  Rectangle = 'Rectangle',
  Square = 'Square',
  Circle = 'Circle',
  Oval = 'Oval',
  Booth = 'Booth',
  CinemaHall = 'CinemaHall'
}

// Table status
export enum TableStatus {
  Available = 1,
  Occupied = 2,
  Reserved = 3,
  OutOfService = 4,
  Cleaning = 5
}

// Core entities
export interface Tenant {
  id: string;
  name: string;
  restaurantCode?: string;
  subdomain: string;
  planId?: string;
  planExpiry?: Date | string;  // subscription expiry date
  isActive: boolean;
  canAdjustSales?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Restaurant {
  id: string;
  tenantId: string;
  name: string;
  restaurantCode?: string;
  address: string;
  phone: string;
  email: string;
  logoUrl?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface User {
  id: string;
  tenantId: string;
  restaurantId?: string;
  username: string;
  email: string;
  passwordHash: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  phone?: string;
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Table {
  id: string;
  restaurantId: string;
  floorId?: string;
  tableNumber: string;
  tableName?: string;
  capacity: number;
  status: 1 | 2 | 3 | 4 | 5;
  location?: string;
  shape?: TableShape;
  xPosition?: number;
  yPosition?: number;
  width?: number;
  height?: number;
  rotation?: number;
  mergedWithId?: string;
  isMerged?: boolean;
  isTemporary?: boolean;
  qrCode?: string;
  qrCodeUrl?: string;
  description?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface TableLayoutUpdate {
  tableId: string;
  floorId?: string;
  shape?: TableShape;
  xPosition?: number;
  yPosition?: number;
  width?: number;
  height?: number;
  rotation?: number;
}

export interface MenuCategory {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface MenuItem {
  id: string;
  categoryId: string;
  name: string;
  shortcutName?: string;
  description?: string;
  price: number;
  imageUrl?: string;
  isAvailable: boolean;
  preparationTime?: number; // in minutes
  isVegetarian: boolean;
  isSpicy: boolean;
  tags?: string[];
  // Optional per-item size options (e.g. S/M/L), each with its own price.
  // Empty/undefined means the item has no sizes and uses its single `price`.
  sizes?: { name: string; price: number }[];
  isHappyHourEnabled?: boolean;
  happyHourPrice?: number | null;
  happyHourStart?: string | null;
  happyHourEnd?: string | null;
  happyHourDays?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface Order {
  id: string;
  restaurantId: string;
  tableId?: string;
  orderNumber: string;
  customerName?: string;
  customerPhone?: string;
  status: OrderStatus;
  totalAmount: number;
  taxAmount: number;
  discountAmount: number;
  finalAmount: number;
  paymentMethod?: PaymentMethod;
  paymentStatus: 'Pending' | 'Paid' | 'Failed';
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  servedAt?: Date;
  paidAt?: Date;
  orderType?: number | string;
  userId?: string | null;
  subtotal?: number;
  orderItems?: OrderItem[];
  isOffline?: boolean;
  source?: number;
  orderDate?: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  menuItemId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  specialInstructions?: string;
  createdAt: Date;
}

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

export interface LoginRequest {
  restaurantCode: string;
  username: string;
  password: string;
  rememberRestaurantCode?: boolean;
}

export interface LoginResponse {
  token: string;
  user: User;
  expiresAt: Date;
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface ReportTemplate {
  id: string;
  restaurantId: string;
  name: string;
  description: string;
  type: number;
  category: number;
  startDate?: string;
  endDate?: string;
  branchId?: string;
  userId?: string;
  groupBy?: string;
  chartType: number;
  columns: string[];
  isScheduled: boolean;
  scheduleFrequency?: number;
  scheduleTime?: string;
  scheduleDay?: number;
  defaultFormat: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ReportExecution {
  id: string;
  reportId: string;
  restaurantId: string;
  requestedBy?: string;
  executedAt: string;
  format: number;
  resultCount: number;
  executionTimeMs: number;
  filePath?: string;
  fileName?: string;
  fileSizeBytes?: number;
  fileData?: string;
  isSuccess: boolean;
  errorMessage?: string;
}

export interface DashboardSnapshot {
  id: string;
  restaurantId: string;
  capturedAt: string;
  todayRevenue: number;
  todayOrders: number;
  todayCovers: number;
  averageOrderValue: number;
  totalTables: number;
  occupiedTables: number;
  availableTables: number;
  occupancyRate: number;
  activeStaff: number;
  peakHour?: string;
  topSellingItems?: string;
  revenueChange: number;
  ordersChange: number;
}

// ── Staff Management ────────────────────────────────────────────────────────

export type StaffPosition = 'Manager' | 'Cashier' | 'Waiter' | 'Kitchen' | string;

export interface StaffMember {
  id: string;
  fullName: string;
  username: string;
  email: string;
  phone?: string;
  role: string;
  position?: StaffPosition;
  isActive: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
}

export interface StaffRole {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: StaffPermission[];
}

export interface StaffPermission {
  id: string;
  module: string;
  name: string;
  code: string;
}

export interface AttendanceRecord {
  staffShiftId: string;
  userId: string;
  fullName: string;
  position?: string;
  shiftName: string;
  date: string;
  status: 'Scheduled' | 'CheckedIn' | 'CheckedOut' | 'OnBreak' | 'Cancelled' | 'ForceClosed';
  actualStartTime: string;
  actualEndTime?: string;
  notes?: string;
}

export interface StaffShift {
  id: string;
  branchId: string;
  branchName: string;
  name: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  isActive: boolean;
  assignedStaff: AssignedStaff[];
}

export interface AssignedStaff {
  staffShiftId: string;
  userId: string;
  fullName: string;
  position?: string;
  status: string;
}

export interface StaffPerformance {
  userId: string;
  fullName: string;
  position?: string;
  totalOrders: number;
  totalSales: number;
  averageOrderValue: number;
  tablesServed: number;
  attendancePercent: number;
  periodStart: string;
  periodEnd: string;
}

export interface StaffCommission {
  id: string;
  userId: string;
  fullName: string;
  position?: string;
  salesAmount: number;
  commissionRate: number;
  commissionAmount: number;
  periodStart: string;
  periodEnd: string;
  isPaid: boolean;
  paidAt?: string;
  createdAt: string;
}

// ── End Staff Management ─────────────────────────────────────────────────────

// POS specific types
export interface CartItem {
  menuItem: MenuItem;
  quantity: number;
  specialInstructions?: string;
}

export interface TableStatusInfo {
  table: Table;
  order?: Order;
  occupiedSince?: Date;
}

// SaaS context
export interface AppContextType {
  currentTenant?: Tenant;
  currentRestaurant?: Restaurant;
  user?: User;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (restaurantCode: string, username: string, password: string, rememberRestaurantCode?: boolean) => Promise<boolean>;
  logout: () => void;
  switchTenant: (tenantId: string) => Promise<void>;
  switchRestaurant: (restaurantId: string) => Promise<void>;
  updateUser: (data: { firstName?: string; lastName?: string; email?: string; username?: string; phone?: string }) => Promise<void>;
}

// Pricing Plan for SuperAdmin
export interface PricingPlan {
  id: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly?: number;
  maxUsers: number;
  maxMenuItems: number;
  maxTables: number;
  hasPOS: boolean;
  hasInventory: boolean;
  hasCRM: boolean;
  hasOnlineOrdering: boolean;
  hasDelivery: boolean;
  hasKDS: boolean;
  hasReports: boolean;
  canAdjustSales: boolean;
  trialDays: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt?: Date;
}

// ── Coupons & Discounts ──────────────────────────────────────────────────────

export type CouponType = 1 | 2 | 3 | 4; // 1=Percentage, 2=FixedAmount, 3=BuyXGetY, 4=FreeDelivery

export interface Coupon {
  id: string;
  restaurantId: string;
  code: string;
  name: string;
  description?: string;
  type: CouponType;
  value: number;
  maxUses?: number;
  currentUses: number;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  startDate?: string;
  endDate?: string;
  validFromTime?: string;
  validToTime?: string;
  applyToAllItems: boolean;
  menuCategoryId?: string;
  menuItemId?: string;
  maxUsesPerUser?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export type DiscountRuleType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
// 1=Weekend, 2=HappyHour, 3=Holiday, 4=HappyDay, 5=RushHour
// 6=EarlyBird, 7=NightOwl, 8=FestivalOffer, 9=BulkDiscount

export interface DiscountRule {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  type: DiscountRuleType;
  dayOfWeek?: number;
  startTime?: string;
  endTime?: string;
  specificDate?: string;
  startDate?: string;
  endDate?: string;
  discountPercentage: number;
  fixedPrice?: number;
  fixedDiscountAmount?: number;
  isIncrease: boolean;
  bulkMinOrderValue?: number;
  applyToAllItems: boolean;
  menuCategoryId?: string;
  menuItemId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ValidationStep {
  stepNumber: number;
  stepName: string;
  passed: boolean;
  message: string;
}

export interface DiscountBreakdown {
  couponType: string;
  orderAmount: number;
  discountValue: number;
  formula: string;
  rawDiscount: number;
  maxCap?: number;
  capNote?: string;
  finalDiscount: number;
  finalPayable: number;
  deliveryCharge: number;
  savings: number;
}

export interface CouponValidationResult {
  isValid: boolean;
  couponId?: string;
  code?: string;
  name?: string;
  description?: string;
  discountLabel?: string;
  discountAmount: number;
  finalOrderAmount: number;
  message: string;
  steps: ValidationStep[];
  failedAtStep?: number;
  failedStepName?: string;
  breakdown?: DiscountBreakdown;
}

export interface CouponStats {
  totalCoupons: number;
  activeCoupons: number;
  totalUsages: number;
  totalDiscountGiven: number;
  topCoupons: Array<{ code: string; name: string; uses: number; discountGiven: number }>;
}

// ── End Coupons & Discounts ──────────────────────────────────────────────────

export interface SubscriptionPaymentPlanFeatures {
  hasPOS: boolean;
  hasKDS: boolean;
  hasInventory: boolean;
  hasCRM: boolean;
  hasOnlineOrdering: boolean;
  hasDelivery: boolean;
  hasReports: boolean;
  maxUsers: number;
  maxMenuItems: number;
  maxTables: number;
}

export interface SubscriptionPayment {
  id: string;
  tenantId: string;
  tenantName?: string;
  tenantIsActive?: boolean;
  pricingPlanId: string;
  pricingPlanName?: string;
  planFeatures?: SubscriptionPaymentPlanFeatures;
  planExpiry?: Date | string;
  amount: number;
  billingCycle: 'monthly' | 'yearly';
  paymentMethod: 'upi' | 'card' | 'bank' | 'cash' | string;
  paymentStatus: 'Completed' | 'Failed' | 'Pending';
  transactionId?: string;
  paymentDate: Date | string;
  createdAt: Date | string;
}

export interface KOTConfig {
  id: string;
  restaurantId: string;
  enableAutoKOT: boolean;
  splitKOTByCategory: boolean;
  requireKOTConfirmation: boolean;
  skipPOSApproval: boolean;
  skipQRApproval: boolean;
  posOrderTargetStatus: number;
  qrOrderTargetStatus: number;
  kotPrintDelay: number;
  defaultPrinterId?: string;
  createdAt?: string;
  updatedAt?: string;
}

